import React, { RefObject } from 'react';
import { FaLock, FaUnlockAlt } from 'react-icons/fa';
import { settings } from '../../settings';
import Fetch from '../../utils/fetch';
import { ReqEnum } from '../../const/requestStatus';
import { Loader } from '../../components/Loader/Loader';
import Alert from '../../components/alerts/Alert';
import SelectCourierModal from '../../components/modals/SelectCourierModal';
import { unique } from '../../utils/array';
import PageVisibility from 'react-page-visibility';
import BundleMenuBar from './components/BundleMenuBar';
import './Bundle.scss';
import {
  BundleJson,
  BundleStatus,
  CarType,
  CourierJson,
  Instruction,
  InstructionType,
  TravelMode,
} from '../../types/bundleTypes';
import {
  DeliveryAddress,
  Order,
  OrderListOrder,
  OrderListOrderClazz,
} from '../../types/orderTypes';
import ChangeShelfModal from '../oldPlanner/modals/ChangeShelfModal';
import AddOrderModal from './components/AddOrderModal';
import Swal from 'sweetalert2';
import { DataBox } from '../../components/DataBox/DataBox';
import { plainToClass } from 'class-transformer';
import BundleMap, {
  OrderInstruction,
  toOrderInstructions,
} from '../../components/maps/BundleMap';
import dayjs from 'dayjs';
import { formatKm } from '../bundles/components/BundleUtils';
import {
  disconnect,
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../utils/pusher';
import {
  bundleChannelSubscription,
  bundleEventName,
} from '../../const/pusherChannelEventConsts';
import { FaRoute } from 'react-icons/all';
import { Helmet } from 'react-helmet';
import {
  DeviationSummary,
  fetchDeviationSummaries,
} from '../order/journey/deviations/fetchDeviations';
import SelectDeliveryDateMultipleModal from '../../components/modals/SelectDeliveryDateMultipleModal';
import { OrderBundle } from 'components/OrderBundle/OrderBundle';
import { reorderDraftNew } from 'pages/oldPlanner/utils/bundleDraft';
import { OrderFilterCheckboxes } from 'components/OrderFilterCheckboxes/OrderFilterCheckboxes';
import { OrderFilterOptions } from 'const/OrderFilterOptions';
import { OrderFilterOptionsType } from 'types/OrderFilterOptionsType';
import { fetchOrderScans } from 'fetch/orderFetch';
import { OrderFilterWithHubScan } from 'utils/OrderFilter/WithHubScan';

class BundleJsonExt extends BundleJson {
  instructions: InstructionJsonExt[];
  xxl: boolean;
}

class InstructionJsonExt extends Instruction {
  position: number;
  location: DeliveryAddress;
  order: Order;
}

interface State {
  showCourierModal: boolean;
  showChangeShelfModal: boolean;
  showAddOrderModal: boolean;
  totalDuration: number;
  totalDistance: number;
  lockMapWhenScroll: boolean;
  stickyMap: boolean;
  markAsPickedUp: boolean;
  markAsDelivered: boolean;
  removeOrders: boolean;
  rescheduleOrders: boolean;
  showNewDeliveryDateModal: boolean;
  selectedOrders: string[];
  bundleAsync: {
    status?: ReqEnum;
    data?: BundleJsonExt;
    courier?: CourierJson;
  };
  isBundleUpdating: boolean;
  warningUpdateModalOpen: boolean;
  showCompleteBundleModal: boolean;
  showCancelBundleModal: boolean;
  animateMarkers: boolean;
  deviationSummaries: Map<string, DeviationSummary[] | undefined>;
  orderInstructions: Array<OrderInstruction>;
  filteredOrderInstructions: Array<OrderInstruction> | null;
  bundleData: BundleJson;
}

interface MatchParams {
  bundleId: string;
}

export interface RouteComponentProps<P> {
  match: Match<P>;
}

export interface Match<P> {
  params: P;
  isExact: boolean;
  path: string;
  url: string;
}

const activeFilterOptions = [
  OrderFilterOptions.WITH_HUB_SCAN,
  OrderFilterOptions.WITHOUT_HUB_SCAN,
];

class Bundle extends React.Component<RouteComponentProps<MatchParams>, State> {
  stickyDiv: RefObject<HTMLDivElement>;
  stickyMapRef: RefObject<HTMLDivElement>;

  constructor(props: RouteComponentProps<MatchParams>) {
    super(props);

    this.state = {
      bundleData: null,
      filteredOrderInstructions: null,
      orderInstructions: [],
      bundleAsync: {
        status: ReqEnum.PENDING,
        data: null,
        courier: null,
      },
      showCourierModal: false,
      showChangeShelfModal: false,
      showAddOrderModal: false,
      totalDuration: 0,
      totalDistance: 0,
      lockMapWhenScroll: false,
      stickyMap: false,
      markAsPickedUp: false,
      markAsDelivered: false,
      removeOrders: false,
      rescheduleOrders: false,
      selectedOrders: [],
      isBundleUpdating: false,
      warningUpdateModalOpen: false,
      showCompleteBundleModal: false,
      showCancelBundleModal: false,
      showNewDeliveryDateModal: false,
      animateMarkers: false,
      deviationSummaries: new Map(),
    };

    this.stickyDiv = React.createRef<HTMLDivElement>();
    this.stickyMapRef = React.createRef<HTMLDivElement>();
  }

  componentDidMount() {
    this.loadBundle();
    window.addEventListener('scroll', this.handleScroll);
    subscribeToChannel(bundleChannelSubscription, bundleEventName, data =>
      this.onSubscriptionToPusher(data)
    );
  }

  onVisibilityChange = (isVisible: boolean) => {
    if (isVisible) {
      if (this.isInEditMode()) {
        this.isBundleUpdated().then(updated => {
          updated && this.fireUpdateWarning(this.state.bundleAsync.data.id);
        });
      } else {
        this.loadBundle();
      }
      subscribeToChannel(bundleChannelSubscription, bundleEventName, data =>
        this.onSubscriptionToPusher(data)
      );
    } else {
      disconnect();
    }
  };

  isInEditMode = (): boolean => {
    const {
      showAddOrderModal,
      showChangeShelfModal,
      markAsDelivered,
      markAsPickedUp,
      removeOrders,
      showCourierModal,
      showCompleteBundleModal,
      showCancelBundleModal,
    } = this.state;

    return (
      showCourierModal ||
      showAddOrderModal ||
      markAsDelivered ||
      markAsPickedUp ||
      showCancelBundleModal ||
      showChangeShelfModal ||
      removeOrders ||
      showCompleteBundleModal
    );
  };

  onSubscriptionToPusher = (data: { bundleId: string; message: string }) => {
    const { bundleId } = this.props.match.params;
    const { warningUpdateModalOpen } = this.state;
    if (data.bundleId === bundleId) {
      if (!warningUpdateModalOpen && this.isInEditMode()) {
        this.setState(
          { isBundleUpdating: true, warningUpdateModalOpen: true },
          () => this.fireUpdateWarning(bundleId)
        );
      }
      this.setState({ isBundleUpdating: true }, () => this.loadBundle());
      console.log('pusher data ', data);
    }
  };

  fireUpdateWarning = (bundleId: string) =>
    Swal.fire({
      title: 'The Bundle has been updated!',
      text: `Please press ok and the bundle ${bundleId} will automatically refresh. After refresh, you will be able to edit bundle again.`,
      icon: 'warning',
      allowOutsideClick: false,
      confirmButtonText: 'Ok',
      confirmButtonColor: '#d33',
    })
      .then(result => {
        this.setState({ warningUpdateModalOpen: false });
        this.loadBundle();
      })
      .catch(error => console.error(error.message));

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    unsubscribeFromChannel(bundleChannelSubscription);
  }

  onCancelModalChange = (status: boolean) => {
    this.setState({ showCancelBundleModal: status });
  };

  handleScroll = () => {
    if (!this.state.lockMapWhenScroll) {
      return;
    }
    if (window.innerHeight < 700) {
      this.setState({ stickyMap: false });
    }

    if (window.pageYOffset >= this.stickyDiv.current.offsetTop) {
      this.setState({ stickyMap: true });
      this.stickyMapRef.current.classList.add('stickToTop');
    } else {
      this.setState({ stickyMap: false });
      this.stickyMapRef.current.classList.remove('stickToTop');
    }
  };

  onUpdateTotalDuration = newDuration => {
    if (newDuration !== this.state.totalDuration) {
      this.setState({ totalDuration: newDuration });
    }
  };

  onUpdateTotalDistance = newDistance => {
    if (newDistance !== this.state.totalDistance) {
      this.setState({ totalDistance: newDistance });
    }
  };

  loadBundle = async () => {
    const { bundleId } = this.props.match.params;
    try {
      const bundleJsonRaw = await Fetch.get2({
        url: `${settings.newApiUrl}/bundle/${bundleId}`,
      });
      const bundle: BundleJson = plainToClass(BundleJson, bundleJsonRaw);

      const orderIds: string[] = unique(
        bundle.instructions.map(instruction => instruction.orderId)
      );
      const assignedCourierId = bundle.assignedCourierId;

      const courier: CourierJson = assignedCourierId
        ? await this.fetchCourier(assignedCourierId)
        : null;
      const orders: Order[] = await this.fetchOrders(orderIds);
      const deviationSummaries = await fetchDeviationSummaries(orderIds);
      const bundleExt = this.addBundleExtraInfo(bundle, orders);

      const orderInstructions = toOrderInstructions(bundle, orders);

      this.setState({
        orderInstructions,
        bundleData: bundle,
        bundleAsync: {
          status: ReqEnum.SUCCESS,
          data: bundleExt,
          courier,
        },
        isBundleUpdating: false,
        deviationSummaries: deviationSummaries,
      });
    } catch (error) {
      this.setState({
        bundleAsync: {
          status: ReqEnum.ERROR,
        },
        isBundleUpdating: false,
      });
    }
  };

  isBundleUpdated = async (): Promise<boolean> => {
    const bundle = this.state.bundleAsync.data;
    try {
      const bundleJsonRaw = await Fetch.get2({
        url: `${settings.newApiUrl}/bundle/${bundle.id}`,
      });
      const updatedBundle: BundleJson = plainToClass(BundleJson, bundleJsonRaw);
      return updatedBundle.versionUuid !== bundle.versionUuid;
    } catch (error) {
      return false;
    }
  };

  fetchOrders = async (orderIds: string[]): Promise<Order[]> => {
    if (orderIds.length === 0) {
      return [];
    }
    const orderIdsParam = orderIds.join(',');
    const response = await Fetch.get({
      url: `${settings.newApiUrl}/order?orderIds=${orderIdsParam}`,
    });
    return response.ok ? await response.json() : [];
  };

  fetchCourier = async (courierId: string): Promise<CourierJson> => {
    const response: Response = await Fetch.get({
      url: `${settings.newApiUrl}/courier/${courierId}`,
    });
    return response.ok ? await response.json() : null;
  };

  addBundleExtraInfo = (bundle: BundleJson, orders: Order[]): BundleJsonExt => {
    const orderTable: { [key: string]: Order } = {};
    orders.forEach(order => order && (orderTable[order.id] = order));

    const bundleExt: BundleJsonExt = Object.assign({}, bundle) as BundleJsonExt;

    let xxl = false;
    bundleExt.instructions = bundle.instructions.map((instruction, i) => {
      const instrExt = Object.assign({}, instruction) as InstructionJsonExt;
      instrExt.position = i + 1;
      const order = orderTable[instruction.orderId];
      if (!order) {
        return instrExt;
      }

      xxl = xxl || order.xxl;
      instrExt.order = order;
      if (instruction.type === InstructionType.Pickup) {
        instrExt.location = {
          address: order.originAddress.fullAddressString,
          name: order.pickupName,
          phone: order.pickupPhone,
          latitude: order.originAddress.location.latitude,
          longitude: order.originAddress.location.longitude,
        };
      } else if (instruction.type === InstructionType.Delivery) {
        instrExt.location = {
          address: order.recipientAddress,
          name: order.recipientName,
          phone: order.recipientPhone,
          latitude: order.recipientLatitude,
          longitude: order.recipientLongitude,
        };
      }
      return instrExt;
    });
    bundleExt.xxl = xxl;

    return plainToClass(BundleJsonExt, bundleExt);
  };

  onSaveDndDraft = async (
    bundle: BundleJson,
    newOrderInstructions: Array<OrderInstruction>
  ) => {
    const instructions = newOrderInstructions.map(
      orderInstruction => orderInstruction.instruction
    );
    try {
      const newBundle: BundleJson = await reorderDraftNew(
        bundle.id,
        instructions
      );
      /* Update the default `orderInstructions` to latest data */
      this.setState({
        bundleData: newBundle,
        orderInstructions: [...newOrderInstructions],
      });
    } catch (error) {
      Swal.fire('Error reordering', (error as Error).message, 'error');

      /* Update the default `orderInstructions` to original data */
      this.setState({
        orderInstructions: [...this.state.orderInstructions],
      });
    }
  };

  onSelectOrderInstruction = (
    orderInstructions: Array<OrderInstruction>
  ): void => {
    const orders = orderInstructions.map(
      orderInstruction => orderInstruction.order.id
    );

    this.setState({
      selectedOrders: orders,
    });
  };

  sortInstructions(instructions: Instruction[], sorted: Instruction[]) {
    const key = (i: Instruction) => `${i.orderId}-${i.type}`;
    const instructionTable: { [key: string]: number } = {};
    sorted.forEach((instr, i) => {
      instructionTable[key(instr)] = i;
    });

    const position = (i: Instruction) => instructionTable[key(i)] || 0;
    instructions.sort((instr1, instr2) => {
      return position(instr1) - position(instr2);
    });
  }

  toBundleStatusMessage(bundle: BundleJson): { title: string; type: string } {
    if (bundle.instructions.length === 0) {
      return { title: 'Cancelled', type: 'danger' };
    }

    switch (bundle.status) {
      case BundleStatus.Draft:
        return { title: 'Draft', type: 'warning' };
      case BundleStatus.Published:
      case BundleStatus.Proposed:
        return { title: 'Ready', type: 'info' };
      case BundleStatus.Accepted:
        return { title: 'In progress', type: 'info' };
      case BundleStatus.Completed:
        return { title: 'Completed', type: 'success' };
      case BundleStatus.Deleted:
        return { title: 'Outdated', type: 'danger' };
      default:
        return { title: bundle.status, type: 'warning' };
    }
  }

  resetSelectionMode() {
    this.setState({
      markAsPickedUp: false,
      markAsDelivered: false,
      removeOrders: false,
      rescheduleOrders: false,
      selectedOrders: [],
    });
  }

  markOrderAs = async (
    urlAction: 'mark-as-ready' | 'mark-as-picked' | 'mark-as-delivered'
  ) => {
    const result = await Fetch.put({
      url: `${settings.newApiUrl}/order/${urlAction}`,

      body: {
        orderIds: this.state.selectedOrders,
      },
    });
    return result.ok;
  };

  completeBundle = async (bundle: BundleJson) => {
    try {
      const response = await Fetch.put({
        url: `${settings.newApiUrl}/bundle/${bundle.id}/complete`,
      });
      if (response.ok || response.status === 204) {
        this.loadBundle();
        return;
      }
    } catch (error) {
      console.error('Error completing bundle', bundle, error);
    }

    Swal.fire(
      'Something went wrong. The bundle could not be set as completed.',
      undefined,
      'error'
    );
  };

  resetAllPickupInstructions = async (bundleId: string) => {
    try {
      const response = await Fetch.put({
        url: `${settings.newApiUrl}/bundle/${bundleId}/reset-pickups`,
      });
      if (response.ok || response.status === 204) {
        this.loadBundle();
      }
    } catch (error) {
      console.error('Error resetting pickups of bundle', bundleId, error);
      Swal.fire(
        'Something went wrong. The bundle could not be set as completed.',
        undefined,
        'error'
      );
    }
  };

  allSelectedOrdersPickedUp(): boolean {
    const selectedOrders = this.state.selectedOrders;
    const bundle = this.state.bundleAsync.data;

    const pickedUpOrderIds = bundle.instructions
      .filter(instr => instr.type === InstructionType.Pickup && instr.completed)
      .map(instr => instr.orderId);
    return selectedOrders.every(orderId => pickedUpOrderIds.includes(orderId));
  }

  async confirmMultipleOrderAction() {
    if (this.state.markAsPickedUp) {
      await this.markOrderAs('mark-as-picked');
    } else if (this.state.markAsDelivered) {
      await this.markOrderAs('mark-as-delivered');
    } else if (this.state.removeOrders) {
      await this.markOrderAs('mark-as-ready');
    }
  }

  selectionActionButtonText = () => {
    const { markAsPickedUp, markAsDelivered, removeOrders, rescheduleOrders } =
      this.state;
    if (markAsPickedUp) {
      return 'Confirm Mark As Picked Up';
    } else if (markAsDelivered) {
      return 'Confirm Mark As Delivered';
    } else if (removeOrders) {
      return 'Confirm Remove Orders';
    } else if (rescheduleOrders) {
      return 'Set new delivery date';
    }
  };

  confirmSelectionAction() {
    if (this.state.selectedOrders.length === 0) {
      Swal.fire(
        'No orders selected',
        'Please select one or more orders from the bundle.',
        'warning'
      );
      return;
    }

    if (this.state.rescheduleOrders) {
      this.setState({ showNewDeliveryDateModal: true });
      return;
    }

    if (this.state.markAsDelivered && !this.allSelectedOrdersPickedUp()) {
      Swal.fire(
        'Delivery Before Pick Up',
        'Some of the orders selected to be Marked as Delivered have not been picked up.',
        'error'
      );
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.selectionActionButtonText(),
    })
      .then(result => {
        if (result.value) {
          this.confirmMultipleOrderAction();

          Swal.fire('Success!', 'The bundle has been updated.', 'success')
            .then(() => this.loadBundle())
            .catch(error => console.error(error.message));
        }

        this.resetSelectionMode();
      })
      .catch(error => console.error(error.message));
  }

  confirmCompleteBundle = () => {
    this.setState({ showCompleteBundleModal: true }, () => {
      const bundle = this.state.bundleAsync.data;
      const hasUncompletedInstructions = bundle.instructions.some(
        instr => !instr.completed
      );

      if (hasUncompletedInstructions) {
        Swal.fire({
          title: 'Complete Bundle',
          text: 'This bundle contains some orders that have not been delivered. Are you sure you want to mark it as completed?',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, do it!',
        })
          .then(result => {
            if (result.value) {
              this.completeBundle(bundle);
            }
            this.setState({ showCompleteBundleModal: false });
          })
          .catch(error => {
            this.setState({ showCompleteBundleModal: true });
            console.error(error.message);
          });
      } else {
        Swal.fire({
          title: 'Complete Bundle',
          text: 'Are you sure you want to mark this bundle as completed?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, do it!',
        })
          .then(result => {
            if (result.value) {
              this.completeBundle(bundle);
            }
            this.setState({ showCompleteBundleModal: true });
          })
          .catch(error => {
            console.error(error.message);
            this.setState({ showCompleteBundleModal: true });
          });
      }
    });
  };

  resetPickups = () => {
    const bundle = this.state.bundleAsync.data;
    Swal.fire({
      title: 'Reset Pickups',
      text: 'Are you sure you want to reset all pickups in this bundle? The courier will need to scan all orders for pickup again.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, do it!',
    }).then(result => {
      if (result.value) {
        this.resetAllPickupInstructions(bundle.id);
      }
    });
  };

  getCourierVehicle(courier: CourierJson): string {
    if (courier.travelMode === TravelMode.BIKE) {
      return 'Bike';
    } else {
      switch (courier.carType) {
        case CarType.ELECTRIC:
          return 'Electric Car';
        case CarType.HYBRID:
          return 'Hybrid Car';
        case CarType.PETROL:
          return 'Petrol Car';
        case CarType.DIESEL:
          return 'Diesel Car';
        default:
          return 'Car';
      }
    }
  }

  handleCheckboxFilter = async activeCheckboxFilters => {
    /* Get only active Filters */
    const filters = activeFilterOptions.filter(
      option => !!activeCheckboxFilters[option]
    );

    /* NOTE :
     * The loop is needed because of current flow :
     * - Fetch Bundle -> contains OrderIDs
     * - Fetch Order API based on the orderIds in Bundle
     */
    let newOrderInstructions: Array<OrderInstruction> = null;
    if (filters.length > 0) {
      const filterAction = async (accumulator, orderInstruction) => {
        const existingAccumulator = await accumulator;
        let filteredOrder: OrderInstruction;

        const order = orderInstruction.order as Order;
        const orderWithHubScan = await OrderFilterWithHubScan(order);

        /* Do Filters */
        filters.forEach(filter => {
          switch (filter) {
            case OrderFilterOptions.WITH_HUB_SCAN:
              if (orderWithHubScan) {
                filteredOrder = orderInstruction;
              }
              break;
            case OrderFilterOptions.WITHOUT_HUB_SCAN:
              if (!orderWithHubScan) {
                filteredOrder = orderInstruction;
              }
              break;
            default:
              filteredOrder = null;
              break;
          }
        });

        if (filteredOrder) {
          existingAccumulator.push(orderInstruction);
        }
        return Promise.resolve(existingAccumulator);
      };
      newOrderInstructions = await this.state.orderInstructions.reduce<
        Promise<Array<OrderInstruction>>
      >(filterAction, Promise.resolve([]));
    }

    this.setState({ filteredOrderInstructions: newOrderInstructions });
  };

  render() {
    const { bundleAsync, animateMarkers } = this.state;
    const bundle = this.state.bundleAsync.data;
    const courier = this.state.bundleAsync.courier;
    const {
      showCourierModal,
      showChangeShelfModal,
      showAddOrderModal,
      lockMapWhenScroll,
      stickyMap,
      markAsPickedUp,
      markAsDelivered,
      removeOrders,
      showNewDeliveryDateModal,
      rescheduleOrders,
      isBundleUpdating,
      deviationSummaries,
      selectedOrders,
    } = this.state;
    const isLoading = [ReqEnum.INIT, ReqEnum.PENDING].includes(
      bundleAsync.status
    );

    if (isLoading) {
      return <Loader type="tile" />;
    }

    if (!bundle) {
      const { bundleId } = this.props.match.params;
      return (
        <>
          <Helmet>
            <title>Bundles</title>
          </Helmet>
          <div
            style={{
              margin: '0px auto',
              paddingTop: 20,
              textAlign: 'center',
            }}
          >
            <h3>No bundle with id {bundleId}</h3>
          </div>
        </>
      );
    }

    const courierName = courier
      ? `${courier.firstName}${courier.lastName ? ` ${courier.lastName}` : ''}`
      : '';
    const courierVehicle = courier ? this.getCourierVehicle(courier) : null;

    const bundleStatus = this.toBundleStatusMessage(bundle);

    const selectIsOpen =
      markAsPickedUp || markAsDelivered || removeOrders || rescheduleOrders;

    const bundleDeliveryDate =
      bundle.instructions.length > 0
        ? dayjs(
            bundle.instructions.filter(
              instruction => instruction.type === InstructionType.Delivery
            )[0].order.deliveryDate
          )
        : null;

    /**
     * Maps the selected order id's to actual orders
     * @returns OrderListOrderClazz[] list of orders selected
     */
    const getOrders = () => {
      return bundle.instructions
        .filter((instruction: Instruction) => instruction.type === 'DELIVERY')
        .filter(
          instruction =>
            selectedOrders.findIndex(s => s === instruction.order.id) !== -1
        )
        .map(i => new OrderListOrderClazz(i.order as OrderListOrder));
    };

    return (
      <>
        <Helmet>
          <title>Bundle - {bundle.id}</title>
        </Helmet>
        <PageVisibility onChange={this.onVisibilityChange}>
          <div className="bundle">
            {isBundleUpdating && (
              <div className="bundle-loader">
                <Loader type="spinner" size="xxl" />
              </div>
            )}
            <BundleMenuBar
              bundle={bundle}
              onShowCourierModal={() => {
                this.setState({ showCourierModal: !showCourierModal });
              }}
              onChangeBundleShelf={() => {
                this.setState({ showChangeShelfModal: !showChangeShelfModal });
              }}
              onAddOrder={() => {
                this.setState({ showAddOrderModal: !showAddOrderModal });
              }}
              onMarkAsPickedUp={() => {
                this.setState({
                  markAsPickedUp: true,
                  markAsDelivered: false,
                  removeOrders: false,
                  selectedOrders: [],
                });
              }}
              onMarkAsDelivered={() => {
                this.setState({
                  markAsPickedUp: false,
                  markAsDelivered: true,
                  removeOrders: false,
                  selectedOrders: [],
                });
              }}
              onRemoveOrders={() => {
                this.setState({
                  markAsPickedUp: false,
                  markAsDelivered: false,
                  removeOrders: true,
                  selectedOrders: [],
                });
              }}
              onRescheduleOrders={() => {
                this.setState({
                  markAsPickedUp: false,
                  markAsDelivered: false,
                  rescheduleOrders: true,
                  selectedOrders: [],
                });
              }}
              onCancelSelectMode={() => {
                this.setState({
                  markAsPickedUp: false,
                  markAsDelivered: false,
                  removeOrders: false,
                  rescheduleOrders: false,
                  selectedOrders: [],
                });
              }}
              onCancelModalChange={this.onCancelModalChange}
              onConfirmSelectMode={this.confirmSelectionAction.bind(this)}
              confirmOrdersButton={selectIsOpen}
              confirmOrdersButtonText={this.selectionActionButtonText()}
              onCompleteBundle={this.confirmCompleteBundle}
              onResetPickups={this.resetPickups}
            />
            <h6>
              <strong>Filters</strong>
            </h6>
            <OrderFilterCheckboxes
              options={activeFilterOptions}
              onFilterChange={this.handleCheckboxFilter}
            />
            <Alert type={bundleStatus.type}>{bundleStatus.title}</Alert>
            <div className="bundle__container">
              <div style={{ flex: 1 }}>
                <div ref={this.stickyDiv} />
                <div
                  style={{ height: stickyMap && lockMapWhenScroll ? 586 : 10 }}
                />
                <div ref={this.stickyMapRef}>
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 10,
                    }}
                  >
                    <div className="u-x1of1 u-x1of1-medium">
                      <DataBox title="Bundle Information">
                        <div className="bundle__container-info">
                          <dl className="definitionlist">
                            <dt>Bundle ID:</dt>
                            <dd>{bundle.id}</dd>
                            <dt>Created date:</dt>
                            <dd>
                              {dayjs(bundle.created).format('DD. MMM YYYY')}
                            </dd>
                            {bundle.instructions &&
                              bundle.instructions.length > 0 && (
                                <>
                                  <dt>Delivery date:</dt>
                                  <dd>
                                    {bundleDeliveryDate.format('DD. MMM YYYY')}
                                  </dd>
                                </>
                              )}
                            <dt>Courier:</dt>
                            <dd>{courierName || 'N/A'}</dd>
                            {courierVehicle && (
                              <>
                                <dt>Courier vehicle:</dt>
                                <dd>{courierVehicle}</dd>
                              </>
                            )}
                          </dl>
                          <dl className="definitionlist">
                            <dt>Shelf:</dt>
                            <dd>{bundle.shelfId || 'N/A'}</dd>
                            <dt>Google driving time:</dt>
                            <dd>{this.state.totalDuration} min</dd>
                            <dt>Total distance:</dt>
                            <dd title={`${this.state.totalDistance} meters`}>
                              {formatKm(this.state.totalDistance)}
                            </dd>
                            <dt>Region:</dt>
                            <dd>{bundle.region}</dd>
                          </dl>
                        </div>
                      </DataBox>
                    </div>
                    <div>
                      {lockMapWhenScroll ? (
                        <FaLock
                          style={{
                            width: 30,
                            height: 30,
                            color: '#b3b3b3',
                            cursor: 'pointer',
                            display: 'block',
                            marginLeft: 3,
                          }}
                          onClick={() =>
                            this.setState({
                              lockMapWhenScroll: !lockMapWhenScroll,
                            })
                          }
                        />
                      ) : (
                        <FaUnlockAlt
                          style={{
                            width: 30,
                            height: 30,
                            color: '#b3b3b3',
                            cursor: 'pointer',
                            display: 'block',
                            marginLeft: 3,
                          }}
                          onClick={() =>
                            this.setState({
                              lockMapWhenScroll: !lockMapWhenScroll,
                            })
                          }
                        />
                      )}
                      <FaRoute
                        style={{
                          width: 30,
                          height: 30,
                          marginTop: 120,
                          color: animateMarkers ? '#000' : '#b3b3b3',
                          cursor: 'pointer',
                          display: 'block',
                          marginLeft: 3,
                        }}
                        onClick={event => {
                          event.stopPropagation();
                          this.setState({
                            animateMarkers: !animateMarkers,
                          });
                        }}
                      />
                    </div>
                  </div>
                  <BundleMap
                    orderInstructions={toOrderInstructions(
                      bundle,
                      bundle.instructions.map(instruction => instruction.order)
                    )}
                    height={600}
                    width={'100%'}
                    courier={courier}
                    durationCallback={this.onUpdateTotalDuration}
                    distanceCallback={this.onUpdateTotalDistance}
                    animate={animateMarkers}
                    deviationSummaries={deviationSummaries}
                  />
                </div>

                <div style={{ height: 10 }} />
              </div>
              <div style={{ flex: 1 }}>
                <OrderBundle
                  hasDragAndDrop={!selectIsOpen}
                  hasCheckbox={selectIsOpen}
                  defaultOrderInstructions={
                    this.state.filteredOrderInstructions ||
                    this.state.orderInstructions
                  }
                  columns={[
                    'InstructionType',
                    'OrderNumber',
                    'WindowTime',
                    'LocationAddress',
                    'EstimateTimeArrival',
                    'OperationType',
                    'Status',
                  ]}
                  bundle={this.state.bundleData}
                  onSaveDndDraft={this.onSaveDndDraft}
                  onSelectOrderInstruction={this.onSelectOrderInstruction}
                />
              </div>
            </div>
            {showCourierModal && (
              <SelectCourierModal
                bundleId={bundle.id}
                onCloseCourierModal={() =>
                  this.setState({
                    showCourierModal: !this.state.showCourierModal,
                  })
                }
                onSelectedCourier={() => {
                  this.setState(
                    { showCourierModal: !this.state.showCourierModal },
                    () => this.loadBundle()
                  );
                }}
                assignedCourierId={
                  bundle.assignedCourierId
                    ? parseInt(bundle.assignedCourierId)
                    : undefined
                }
                region={bundle.region}
              />
            )}
            {showChangeShelfModal && (
              <ChangeShelfModal
                bundle={bundle}
                onCloseModal={() =>
                  this.setState({ showChangeShelfModal: false })
                }
                onSelectModal={() =>
                  this.setState({ showChangeShelfModal: false })
                }
              />
            )}
            {showAddOrderModal && (
              <AddOrderModal
                bundle={this.state.bundleAsync.data}
                onCloseModal={() => this.setState({ showAddOrderModal: false })}
                onSelectModal={() =>
                  this.setState({ showAddOrderModal: false }, () =>
                    this.loadBundle()
                  )
                }
              />
            )}
            {showNewDeliveryDateModal && (
              <SelectDeliveryDateMultipleModal
                orders={getOrders()}
                triggerReload={true}
                onCloseModal={() =>
                  this.setState({
                    showNewDeliveryDateModal: false,
                    rescheduleOrders: false,
                  })
                }
              />
            )}
          </div>
        </PageVisibility>
      </>
    );
  }
}

export default Bundle;
